import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  LoadingSkeleton,
  Pagination,
  Select,
} from '@nexora/design-system';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET /logistics/ctes            → { data: CteDto[], total, page, limit, totalPages }
 * GET /logistics/ctes/dashboard  → { valorTotal, quantidadeTotal, ativos, porStatus[] }
 * PATCH /logistics/ctes/:id/status → atualiza situação
 * POST /logistics/ctes/:id/cancel  → cancela CT-e
 */

interface CteDto {
  id: string;
  number: string;
  serie?: string | null;
  accessKey?: string | null;
  status: string;
  issuerId: string;
  issuerName?: string | null;
  recipientId: string;
  recipientName?: string | null;
  issuedAt?: string | null;
  value?: number | null;
  freightValue?: number | null;
  route?: string | null;
  notes?: string | null;
  createdAt: string;
}

interface DashboardDto {
  valorTotal: number;
  quantidadeTotal: number;
  ativos: number;
  porStatus: { status: string; count: number }[];
}

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral' | 'processing' | 'critical'> = {
  DRAFT: 'neutral',
  EMITIDO: 'info',
  TRANSPORTE: 'processing',
  ENTREGUE: 'success',
  CANCELADO: 'error',
  ENCERRADO: 'neutral',
};

export function CtesPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const limit = 20;

  const params = useMemo(() => {
    const p: Record<string, string> = { page: String(page), limit: String(limit) };
    if (statusFilter !== 'ALL') p['status'] = statusFilter;
    if (search) p['search'] = search;
    return p;
  }, [page, statusFilter, search]);

  const listQ = useQuery({
    queryKey: ['logistics', 'ctes', params],
    queryFn: async () => {
      const data = await apiClient.get<{ data: CteDto[]; total: number }>('/logistics/ctes', { params });
      return data;
    },
    enabled: !!tenantId,
  });

  const dashQ = useQuery({
    queryKey: ['logistics', 'ctes', 'dashboard'],
    queryFn: async () => {
      const data = await apiClient.get<DashboardDto>('/logistics/ctes/dashboard');
      return data;
    },
    enabled: !!tenantId,
  });

  const updateStatus = async (id: string, status: string): Promise<void> => {
    await apiClient.patch(`/logistics/ctes/${id}/status`, { status });
    await listQ.refetch();
    await dashQ.refetch();
  };

  const cancelCte = async (id: string): Promise<void> => {
    await apiClient.post(`/logistics/ctes/${id}/cancel`, { reason: 'Cancelado pela interface' });
    await listQ.refetch();
    await dashQ.refetch();
  };

  const dash = dashQ.data;

  return (
    <Card variant="outlined" padding="0" className="page-card">
      <CardHeader
        className="px-md pt-md"
        action={<Button onClick={() => void navigate({ to: '/logistics/ctes/new' as never })}>Novo CT-e</Button>}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--on-surface)' }}>CT-e Management</h3>
          <p style={{ margin: '0.125rem 0 0', fontSize: '0.875rem', color: 'var(--on-surface-variant)' }}>
            Conhecimentos de Transporte eletrônicos
          </p>
        </div>
      </CardHeader>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', padding: '1rem 1.25rem' }}>
        <Card variant="outlined" padding="1rem">
          <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)' }}>Valor Total</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {dashQ.isLoading ? '—' : formatCurrency(dash?.valorTotal ?? 0)}
          </div>
        </Card>
        <Card variant="outlined" padding="1rem">
          <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)' }}>Quantidade</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{dash?.quantidadeTotal ?? '—'}</div>
        </Card>
        <Card variant="outlined" padding="1rem">
          <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)' }}>Ativos</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{dash?.ativos ?? '—'}</div>
        </Card>
        <Card variant="outlined" padding="1rem">
          <div style={{ fontSize: '0.75rem', color: 'var(--on-surface-variant)' }}>Por Status</div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
            {(dash?.porStatus ?? []).map((s) => (
              <Badge key={s.status} variant={STATUS_VARIANT[s.status] ?? 'neutral'} size="sm">
                {s.status}: {s.count}
              </Badge>
            ))}
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '0.75rem', padding: '0 1.25rem 1rem', flexWrap: 'wrap' }}>
        <Select
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="ALL">Todos</option>
          <option value="DRAFT">Rascunho</option>
          <option value="EMITIDO">Emitido</option>
          <option value="TRANSPORTE">Em transporte</option>
          <option value="ENTREGUE">Entregue</option>
          <option value="CANCELADO">Cancelado</option>
        </Select>
        <Input
          label="Buscar"
          placeholder="Número, emitente, destinatário…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ minWidth: 240 }}
        />
      </div>

      <CardBody>
        {listQ.isLoading ? (
          <LoadingSkeleton />
        ) : listQ.isError ? (
          <div role="alert" style={{ color: 'var(--error)' }}>Erro ao carregar CT-es.</div>
        ) : (listQ.data?.data?.length ?? 0) === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--on-surface-variant)' }}>
            Nenhum CT-e encontrado.
          </div>
        ) : (
          <table className="ds-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Number</th>
                <th>Issuer</th>
                <th>Recipient</th>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Value (R$)</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(listQ.data?.data ?? []).map((cte) => (
                <tr key={cte.id}>
                  <td style={{ fontFamily: "'JetBrains Mono', monospace" }}>{cte.number}</td>
                  <td>{cte.issuerName ?? cte.issuerId.slice(0, 8)}</td>
                  <td>{cte.recipientName ?? cte.recipientId.slice(0, 8)}</td>
                  <td>{cte.issuedAt ? formatDate(cte.issuedAt) : '—'}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(cte.value ?? 0)}</td>
                  <td><Badge variant={STATUS_VARIANT[cte.status] ?? 'neutral'} size="sm">{cte.status}</Badge></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {cte.status === 'DRAFT' && (
                      <Button size="sm" variant="secondary" onClick={() => void updateStatus(cte.id, 'EMITIDO')}>Emitir</Button>
                    )}
                    {cte.status === 'EMITIDO' && (
                      <Button size="sm" variant="secondary" onClick={() => void updateStatus(cte.id, 'TRANSPORTE')}>Transporte</Button>
                    )}
                    {cte.status === 'TRANSPORTE' && (
                      <Button size="sm" variant="secondary" onClick={() => void updateStatus(cte.id, 'ENTREGUE')}>Entregue</Button>
                    )}
                    {(cte.status === 'DRAFT' || cte.status === 'EMITIDO') && (
                      <Button size="sm" variant="outline" onClick={() => void cancelCte(cte.id)}>Cancelar</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>

      {listQ.data && listQ.data.total > limit && (
        <div style={{ padding: '0.75rem 1.25rem' }}>
          <Pagination
            page={page}
            totalPages={Math.ceil(listQ.data.total / limit)}
            onChange={(p) => setPage(p)}
          />
        </div>
      )}
    </Card>
  );
}